import { useEffect, useState } from "react";
import { Show } from "@refinedev/antd";
import { HttpError, useCreate, useCustom } from "@refinedev/core";
import { Button, Card, message, Switch, Typography } from "antd";
import { AppTitle } from "../../../components";
import type { AppIconSettings } from "../Providers/app-icon-data-provider";

const { Text } = Typography;

/** Коды приходят с бэка, а подпись и картинку знаем только мы. */
const ICONS: Record<string, { label: string; preview: string }> = {
  FNL: { label: "Обычная", preview: "/images/app-icons/fnl.png" },
  ANNIVERSARY_15: {
    label: "15 лет ФНЛ",
    preview: "/images/app-icons/anniversary-15.png",
  },
  NEW_YEAR: { label: "Новогодняя", preview: "/images/app-icons/new-year.png" },
};

export const AppIconPage = () => {
  const [defaultIcon, setDefaultIcon] = useState<string>();
  const [clubIconsEnabled, setClubIconsEnabled] = useState(true);
  const [availableIcons, setAvailableIcons] = useState<string[]>([]);

  const { data, isLoading, refetch } = useCustom<AppIconSettings>({
    url: "settings",
    method: "get",
    dataProviderName: "appIconDataProvider",
  });

  const { mutate, isLoading: isSaving } = useCreate();

  useEffect(() => {
    const settings = data?.data;
    if (!settings) {
      return;
    }
    setDefaultIcon(settings.defaultIcon);
    setClubIconsEnabled(settings.clubIconsEnabled);
    setAvailableIcons(settings.availableIcons ?? []);
  }, [data]);

  const onSave = () => {
    if (!defaultIcon) {
      return;
    }

    mutate(
      {
        dataProviderName: "appIconDataProvider",
        resource: "app-icon",
        values: { defaultIcon, clubIconsEnabled },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => {
          message.success("Сохранено");
          refetch();
        },
        onError: (error: HttpError) => {
          message.error(
            error?.response?.data?.errorMessage ??
              "Не удалось сохранить настройки"
          );
        },
      }
    );
  };

  return (
    <Show
      title={<AppTitle>Иконка приложения</AppTitle>}
      isLoading={isLoading}
      headerButtons={() => null}
    >
      <Text type="secondary">
        Иконку по умолчанию видят те, у кого не выбран любимый клуб, и все
        подряд, если клубные иконки выключены. Меняется у человека при первом
        запуске приложения.
      </Text>

      <div
        style={{ display: "flex", gap: 16, flexWrap: "wrap", margin: "24px 0" }}
      >
        {availableIcons.map((code) => {
          const icon = ICONS[code];
          const isActive = code === defaultIcon;

          return (
            <Card
              key={code}
              hoverable
              onClick={() => setDefaultIcon(code)}
              bodyStyle={{ padding: 16, textAlign: "center" }}
              style={{
                width: 160,
                borderColor: isActive ? "#00C7B1" : undefined,
                borderWidth: isActive ? 2 : 1,
              }}
            >
              <img
                src={icon?.preview}
                alt={icon?.label ?? code}
                style={{ width: 96, height: 96, borderRadius: 20 }}
              />
              <div style={{ marginTop: 12 }}>{icon?.label ?? code}</div>
            </Card>
          );
        })}
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <Switch checked={clubIconsEnabled} onChange={setClubIconsEnabled} />
        <span>Клубные иконки</span>
        <Text type="secondary">
          Выключить — у всех встанет иконка по умолчанию, даже если выбран клуб.
        </Text>
      </div>

      <Button
        type="primary"
        onClick={onSave}
        loading={isSaving}
        disabled={!defaultIcon}
        style={{ marginTop: 24 }}
      >
        Сохранить
      </Button>
    </Show>
  );
};
